// Carloo GPS tracker — web + Capacitor native, offline-resilient
//
// Behaviour:
//   • Web (browser preview): uses navigator.geolocation.watchPosition + interval polling
//     + IndexedDB queue
//   • Capacitor native build: uses @capacitor-community/background-geolocation (true OS-level
//     foreground service that survives app suspend, phone calls, SIM swaps and screen-off)
//     + IndexedDB queue
//
// RECONNECT / SYNC LOGIC:
//   • Adaptive sync timer:
//       - 10 s while there is a backlog OR while we are offline → catch up fast
//       - 30 s once the queue is clean and we're online → idle pings
//   • Listens to **multiple reconnect signals** so the queue starts flushing
//     within ~1 s of getting any kind of network back:
//       1. window 'online' event (browser + WebView)
//       2. document 'visibilitychange' (resumes when phone screen is unlocked
//          or after a SIM-swap phone call ends)
//       3. Capacitor App 'resume' event (after app pause / phone-call interrupt)
//       4. Capacitor Network plugin 'networkStatusChange' event (Android-side
//          connectivity manager — fires faster than navigator.onLine)
//   • Bulk batch sync to /api/gps/track-batch (up to 100 points per request)
//     so a long-offline backlog clears in seconds rather than minutes.
//
// Public API (unchanged):
//   startGpsTracking({ visitIdRef, intervalSeconds })
//   stopGpsTracking()
//   getQueueSize()
//   flushGpsQueue()

const DB_NAME = 'carloo_gps';
const STORE = 'queue';
const ACTIVE_VISIT_KEY = 'carloo_active_visit_id';
const BATCH_SIZE = 100;
const INTERVAL_FAST_MS = 10_000;
const INTERVAL_IDLE_MS = 30_000;
// v2.6.4 — server errors back off exponentially (30 s → 60 → 120 → 240 →
// 300 s cap) instead of retrying every 10 s. The 10 s "fast" cadence was
// amplifying every morning slowdown into a fleet-wide retry storm.
const BACKOFF_MAX_MS = 300_000;
const FETCH_TIMEOUT_MS = 25_000;
let consecutiveFailures = 0;

// ---- Capacitor detection ----
const isNative = () => !!(typeof window !== 'undefined' && window.Capacitor && window.Capacitor.isNativePlatform && window.Capacitor.isNativePlatform());

let bgGeo = null;
let bgWatcherId = null;
let webWatcherId = null;
let webIntervalId = null;
let syncTimer = null;
let listenerHandles = [];        // unsubscribe handles for native plugin listeners
let domListenersAttached = false;
let lastFlushOk = true;          // true if last flush completed without errors

let _activeVisitId = (typeof localStorage !== 'undefined') ? (localStorage.getItem(ACTIVE_VISIT_KEY) || null) : null;

export function setActiveVisitId(visitId) {
  _activeVisitId = visitId || null;
  try {
    if (typeof localStorage === 'undefined') return;
    if (_activeVisitId) localStorage.setItem(ACTIVE_VISIT_KEY, _activeVisitId);
    else localStorage.removeItem(ACTIVE_VISIT_KEY);
  } catch {}
}
export function getActiveVisitId() { return _activeVisitId; }

// ---- IndexedDB queue ----
function openDb() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: 'local_id', autoIncrement: true });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function enqueue(point) {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite');
    tx.objectStore(STORE).add(point);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

async function readBatch(limit = BATCH_SIZE) {
  const db = await openDb();
  return new Promise((resolve) => {
    const tx = db.transaction(STORE, 'readonly');
    const req = tx.objectStore(STORE).getAll(undefined, limit);
    req.onsuccess = () => resolve(req.result || []);
    req.onerror = () => resolve([]);
  });
}

async function readAll() {
  const db = await openDb();
  return new Promise((resolve) => {
    const tx = db.transaction(STORE, 'readonly');
    const req = tx.objectStore(STORE).getAll();
    req.onsuccess = () => resolve(req.result || []);
    req.onerror = () => resolve([]);
  });
}

async function removeIds(ids) {
  if (!ids.length) return;
  const db = await openDb();
  const tx = db.transaction(STORE, 'readwrite');
  const store = tx.objectStore(STORE);
  ids.forEach(id => store.delete(id));
  return new Promise((resolve) => { tx.oncomplete = () => resolve(); });
}

// ---- Sync queue → backend ----
function getApiUrl() { return process.env.REACT_APP_BACKEND_URL; }

let _flushInFlight = null;
async function flush() {
  if (_flushInFlight) return _flushInFlight;
  _flushInFlight = (async () => {
    const token = (typeof localStorage !== 'undefined') ? localStorage.getItem('token') : null;
    if (!token) return;

    // Drain in batches of 100. Each successful batch removes those entries
    // from IndexedDB before reading the next batch — ensures we don't loop
    // forever on a transient failure.
    while (true) {
      let batch;
      try { batch = await readBatch(BATCH_SIZE); } catch { break; }
      if (!batch || batch.length === 0) { lastFlushOk = true; break; }

      const points = batch.map(b => ({
        visit_id: b.visit_id || null,
        latitude: b.latitude,
        longitude: b.longitude,
        timestamp: b.timestamp,
      }));

      try {
        const ctrl = (typeof AbortController !== 'undefined') ? new AbortController() : null;
        const timeoutId = ctrl ? setTimeout(() => ctrl.abort(), FETCH_TIMEOUT_MS) : null;
        let r;
        try {
          r = await fetch(`${getApiUrl()}/api/gps/track-batch`, {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
            body: JSON.stringify({ points }),
            signal: ctrl ? ctrl.signal : undefined,
          });
        } finally {
          if (timeoutId) clearTimeout(timeoutId);
        }
        if (r.ok) {
          await removeIds(batch.map(b => b.local_id));
          lastFlushOk = true;
          consecutiveFailures = 0;
          // continue loop to drain rest of queue
        } else if (r.status === 401 || r.status === 403) {
          // Token expired — stop trying until user logs in again
          lastFlushOk = false;
          break;
        } else {
          // Server-side error — back off; will retry on next interval
          lastFlushOk = false;
          consecutiveFailures += 1;
          break;
        }
      } catch {
        // Network failure / timeout — back off; will retry on next interval / online event
        lastFlushOk = false;
        consecutiveFailures += 1;
        break;
      }
    }

    // Adapt sync cadence based on success
    rescheduleSyncTimer();
  })();
  try { return await _flushInFlight; }
  finally { _flushInFlight = null; }
}

function rescheduleSyncTimer() {
  if (syncTimer) clearInterval(syncTimer);
  // Fast cadence while we have a backlog OR last flush failed.
  // Idle cadence once queue is clean.
  const isOffline = (typeof navigator !== 'undefined' && navigator.onLine === false);
  let interval = INTERVAL_IDLE_MS;
  if (isOffline) {
    interval = INTERVAL_FAST_MS;            // offline: cheap local tick, no server load
  } else if (!lastFlushOk) {
    interval = Math.min(BACKOFF_MAX_MS, INTERVAL_IDLE_MS * (2 ** Math.max(0, consecutiveFailures - 1)));
  }
  // v2.4.9 (Feb 26, 2026) — apply ±20 % random jitter so the whole
  // ~800-device fleet doesn't flush GPS points to /gps/track-batch in
  // lockstep every 30 s (root cause of the morning burst per RCA).
  const spread = interval * 0.2;
  const jittered = Math.max(1_000, interval + (Math.random() * 2 - 1) * spread);
  syncTimer = setInterval(() => { flush().catch(() => {}); }, jittered);
}

async function recordPoint(lat, lon, ts) {
  await enqueue({
    visit_id: _activeVisitId,
    latitude: lat,
    longitude: lon,
    timestamp: ts || new Date().toISOString(),
  });
}

// ---- Native (Capacitor) starter ----
// Jun 30, 2026 — added the heartbeatTimer below after a real-world test
// where two staff (AZAD, JAYASURAA) sat / moved <20m for ~10 min outside
// the geofence and the mobile app sent ZERO pings, so the Unplan-Visit
// gates never saw them outside. The native plugin's distanceFilter alone
// (set to 20 m) means a stationary user emits no points; the heartbeat
// guarantees at least one ping every 60 s regardless of movement.
//
// Adaptive cadence (Jun 30, 2026 follow-up — same user, same day):
// "if have gps gaps repeat the check every 30s and fix gps gaps". When
// the last successful ping is >90 s old the timer switches from the
// HEARTBEAT_NORMAL cadence (60 s) to HEARTBEAT_FAST (30 s) so we can
// recover sooner from a permission prompt / battery-saver pause /
// network hiccup. Once a fresh ping lands, we restore the normal cadence.
const HEARTBEAT_NORMAL_MS = 60_000;
const HEARTBEAT_FAST_MS = 30_000;
// If the last successful read is older than this, we're in a gap.
const GAP_DETECT_MS = 90_000;
const HEARTBEAT_INTERVAL_MS = HEARTBEAT_NORMAL_MS;  // kept for tests / external readers
let heartbeatTimer = null;
let heartbeatCurrentMs = HEARTBEAT_NORMAL_MS;
let lastHeartbeatOkAt = 0;  // epoch-ms of the last successful position read

async function captureHeartbeatFix() {
  // Best-effort one-shot position read. Used by the time-based heartbeat
  // to keep pings flowing even when the staff is stationary (web idle on
  // a desk, mobile sitting in a café, etc.). Falls back across:
  //   1. Capacitor Geolocation (works on iOS/Android foreground+background)
  //   2. navigator.geolocation (web + Capacitor webview)
  // Errors are swallowed — a failed heartbeat just leaves the queue empty
  // and the next tick retries.
  try {
    if (isNative()) {
      const { Geolocation } = await import('@capacitor/geolocation');
      const pos = await Geolocation.getCurrentPosition({
        enableHighAccuracy: true,
        timeout: 15000,
        maximumAge: 5000,
      });
      if (pos && pos.coords) {
        await recordPoint(pos.coords.latitude, pos.coords.longitude);
        lastHeartbeatOkAt = Date.now();
        return;
      }
    }
  } catch {}
  // Web fallback (also catches Capacitor webview when @capacitor/geolocation
  // is unavailable).
  if (typeof navigator !== 'undefined' && navigator.geolocation) {
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        await recordPoint(pos.coords.latitude, pos.coords.longitude);
        lastHeartbeatOkAt = Date.now();
      },
      () => {},
      { enableHighAccuracy: true, maximumAge: 5000, timeout: 15000 }
    );
  }
}

function rescheduleHeartbeat() {
  // Adaptive cadence: fast (30 s) when we have a gap, normal (60 s)
  // otherwise. ``lastHeartbeatOkAt == 0`` covers the just-started case
  // (no successful read yet) — we use fast cadence for the first minute
  // so the user gets an early fix instead of waiting a full 60 s.
  const since = lastHeartbeatOkAt ? Date.now() - lastHeartbeatOkAt : Infinity;
  const desired = since > GAP_DETECT_MS ? HEARTBEAT_FAST_MS : HEARTBEAT_NORMAL_MS;
  if (desired === heartbeatCurrentMs && heartbeatTimer !== null) return;
  if (heartbeatTimer !== null) clearInterval(heartbeatTimer);
  heartbeatCurrentMs = desired;
  heartbeatTimer = setInterval(async () => {
    await captureHeartbeatFix();
    rescheduleHeartbeat();
  }, desired);
}

function startHeartbeat() {
  if (heartbeatTimer !== null) return;
  // Fire one immediately so the first ping isn't 60 s after login.
  (async () => {
    await captureHeartbeatFix();
    rescheduleHeartbeat();
  })().catch(() => {});
  // Bootstrap the timer at NORMAL cadence — first reschedule call above
  // will downgrade to FAST if the first capture failed.
  rescheduleHeartbeat();
}

function stopHeartbeat() {
  if (heartbeatTimer !== null) {
    clearInterval(heartbeatTimer);
    heartbeatTimer = null;
  }
  heartbeatCurrentMs = HEARTBEAT_NORMAL_MS;
  lastHeartbeatOkAt = 0;
}

async function startNative() {
  if (bgGeo && bgWatcherId !== null) return true;
  try {
    const { registerPlugin } = await import('@capacitor/core');
    bgGeo = registerPlugin('BackgroundGeolocation');

    bgWatcherId = await bgGeo.addWatcher(
      {
        backgroundMessage: 'Tracking active. Your visits and breaks are being recorded.',
        backgroundTitle: 'Carloo Tex',
        requestPermissions: true,
        stale: false,
        // Feb 22, 2026 — dropped distanceFilter from 20 m → 0 m after the
        // AZAD 4:30 pm miss on prod (Android Battery Saver killed the JS
        // heartbeat entirely, so a stationary/short-move staff outside the
        // geofence emitted ZERO pings). With distanceFilter=0 the native
        // OS-level foreground service emits every OS fix (typically every
        // 10–30 s) regardless of movement — completely independent of the
        // JS event loop / Doze / App Standby throttling. Battery cost is
        // ~5-10% higher per day; acceptable trade-off for reliable Unplan
        // Visit detection which is the app's core value.
        distanceFilter: 0,
      },
      async (location, error) => {
        if (error) return;
        if (!location) return;
        await recordPoint(
          location.latitude,
          location.longitude,
          new Date(location.time || Date.now()).toISOString()
        );
        // Try to flush immediately — cheap if we're online, no-op otherwise
        flush().catch(() => {});
      }
    );
    return true;
  } catch (e) {
    bgGeo = null;
    bgWatcherId = null;
    if (typeof console !== 'undefined') {
      // eslint-disable-next-line no-console
      console.warn('[gpsTracker] Native BackgroundGeolocation unavailable, using web fallback:', e?.message || e);
    }
    return false;
  }
}

async function stopNative() {
  if (bgGeo && bgWatcherId !== null) {
    try { await bgGeo.removeWatcher({ id: bgWatcherId }); } catch {}
    bgWatcherId = null;
  }
}

// ---- Web (browser) starter ----
const WEB_PING_INTERVAL_MS = 30_000;

function webOneShot() {
  if (!navigator.geolocation) return;
  navigator.geolocation.getCurrentPosition(
    async (pos) => { await recordPoint(pos.coords.latitude, pos.coords.longitude); },
    () => {},
    { enableHighAccuracy: true, maximumAge: 10000, timeout: 15000 }
  );
}

function startWeb() {
  if (!navigator.geolocation) return;
  if (webIntervalId === null) {
    webOneShot();
    webIntervalId = setInterval(webOneShot, WEB_PING_INTERVAL_MS);
  }
  if (webWatcherId === null) {
    webWatcherId = navigator.geolocation.watchPosition(
      async (pos) => { await recordPoint(pos.coords.latitude, pos.coords.longitude); },
      () => {},
      { enableHighAccuracy: true, maximumAge: 5000, timeout: 15000 }
    );
  }
}

function stopWeb() {
  if (webWatcherId !== null) {
    try { navigator.geolocation.clearWatch(webWatcherId); } catch {}
    webWatcherId = null;
  }
  if (webIntervalId !== null) {
    clearInterval(webIntervalId);
    webIntervalId = null;
  }
}

// ---- Reconnect listeners ----
function attachReconnectListeners() {
  if (domListenersAttached) return;
  domListenersAttached = true;

  // 1. Browser online event
  window.addEventListener('online', () => { flush().catch(() => {}); });

  // 2. Visibility — when user returns to the tab / unlocks phone
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) flush().catch(() => {});
  });

  // 3. Capacitor App resume + 4. Network plugin (native only)
  (async () => {
    if (!isNative()) return;
    try {
      const { App } = await import('@capacitor/app');
      const handle = await App.addListener('resume', () => { flush().catch(() => {}); });
      listenerHandles.push(handle);
    } catch {}
    try {
      const { Network } = await import('@capacitor/network');
      const handle = await Network.addListener('networkStatusChange', (status) => {
        if (status?.connected) flush().catch(() => {});
      });
      listenerHandles.push(handle);
    } catch {}
  })();
}

function detachReconnectListeners() {
  // We deliberately leave the DOM listeners — they're idempotent and small.
  listenerHandles.forEach(h => { try { h?.remove?.(); } catch {} });
  listenerHandles = [];
}

// ---- Public API ----
export async function startGpsTracking({ intervalSeconds = 30 } = {}) {  // eslint-disable-line no-unused-vars
  let nativeOk = false;
  if (isNative()) {
    nativeOk = await startNative();
  }
  if (!nativeOk) {
    startWeb();
  }
  // Jun 30, 2026 — heartbeat runs in BOTH modes. Native: guarantees ping
  // every 60 s even when staff is stationary (distanceFilter=20m sees no
  // motion). Web: a belt-and-braces sibling to webIntervalId in case the
  // 30 s setInterval is throttled by a backgrounded tab.
  startHeartbeat();
  rescheduleSyncTimer();
  attachReconnectListeners();
  flush().catch(() => {});

  // Feb 22, 2026 — v2.4.0 belt-and-braces: register the staff's assigned
  // office fence with Android's OS-level GeofencingClient. If our
  // foreground service is later killed by battery-saver / App Standby,
  // the OS itself still fires ENTER/EXIT events → the backend gate can
  // still catch the excursion. Fire-and-forget; never blocks tracking.
  import('./nativeGeofence').then((m) => m.bootNativeGeofences()).catch(() => {});
}

export async function stopGpsTracking() {
  if (isNative()) await stopNative();
  else stopWeb();
  stopHeartbeat();
  if (syncTimer) {
    clearInterval(syncTimer);
    syncTimer = null;
  }
  detachReconnectListeners();

  // Tear down OS-level geofences too — otherwise they'd keep firing to the
  // backend after logout with a stale JWT.
  import('./nativeGeofence').then((m) => m.teardownNativeGeofences()).catch(() => {});
}

export async function getQueueSize() {
  const queue = await readAll();
  return queue.length;
}

export function getTrackerState() {
  if (bgWatcherId !== null) return { mode: 'native', active: true };
  if (webWatcherId !== null) return { mode: 'web', active: true };
  return { mode: 'idle', active: false };
}

export { flush as flushGpsQueue };
